import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, GraduationCap, ThumbsUp, ThumbsDown, Wrench, RotateCcw, CheckCircle2, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import ModalCloseButton from '@/components/ModalCloseButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round P108ap — Ticker auto-link (same lightweight parser used in
// AdminHowToFeedbackButton). Detects 1–5 letter all-caps tokens in
// comment bodies so an admin can drill from a comment straight into
// the offending ticker's Stock Modal.
const TICKER_DENYLIST = new Set([
  'I','A','AM','AN','AS','AT','BE','BY','DO','GO','HE','IF','IN','IS','IT','ME',
  'MY','NO','OF','OH','OK','ON','OR','SO','TO','UP','US','WE',
  'ALL','AND','ARE','BUT','CAN','DID','FOR','GET','GOT','HAD','HAS','HER',
  'HIM','HIS','HOW','ITS','LET','MAY','NOR','NOT','NOW','OUR','OUT','PUT',
  'SAW','SAY','SEE','SHE','THE','TOO','TRY','TWO','USE','WAS','WAY','WHO',
  'WHY','YES','YET','YOU',
  'BEEN','BOTH','EACH','EVEN','FROM','HAVE','HERE','JUST','LIKE','MAKE',
  'MANY','MORE','MOST','MUCH','MUST','ONCE','ONLY','OVER','SOME','SUCH',
  'TAKE','THAN','THAT','THEM','THEN','THEY','THIS','VERY','WANT','WERE',
  'WHAT','WHEN','WILL','WITH','YOUR','GOOD','SURE','OKAY','YEAH','PAGE',
  'ABOUT','AFTER','AGAIN','BEING','COULD','EVERY','FIRST','GREAT','HELLO',
  'NEVER','OTHER','RIGHT','THESE','THINK','THOSE','THREE','UNDER','WHERE',
  'WHICH','WHILE','WORLD','WOULD','HOWTO','TODAY','MAYBE',
  'DRIP','DIVIDRIP','LEARN','GUIDE','SEE','READ','SITE','HOME','LINK','MENU',
]);
const TICKER_RE = /\b([A-Z]{1,5}(?:[.\-][A-Z]{1,2})?)\b/g;

function parseCommentSegments(raw) {
  if (!raw || typeof raw !== 'string') return [{ type: 'text', value: raw || '' }];
  const segments = [];
  let lastIndex = 0;
  for (const match of raw.matchAll(TICKER_RE)) {
    const token = match[1];
    const start = match.index;
    const end = start + match[0].length;
    const prevChar = start > 0 ? raw[start - 1] : '';
    if (prevChar === '$' || prevChar === '#') continue;
    if (TICKER_DENYLIST.has(token)) continue;
    if (start > lastIndex) segments.push({ type: 'text', value: raw.slice(lastIndex, start) });
    segments.push({ type: 'ticker', value: token });
    lastIndex = end;
  }
  if (lastIndex < raw.length) segments.push({ type: 'text', value: raw.slice(lastIndex) });
  return segments.length ? segments : [{ type: 'text', value: raw }];
}

/**
 * Human-readable label from a guide path. Turns
 *   `/learn/is-9-percent-yield-safe`  → `Is 9 Percent Yield Safe`
 * so admins reading the modal don't have to parse the URL.
 */
function labelFromPath(path) {
  if (!path) return '(unknown)';
  const slug = path.replace(/^\/learn\/?/i, '').replace(/\/+$/, '');
  if (!slug) return '(root)';
  return slug
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

/**
 * AdminLearnGuideFeedbackButton — Round P108ap (Feb 2026).
 *
 * Admin-only button that opens a scrollable modal showing per-guide
 * thumbs-up / down counts from every /learn/* page, plus the most
 * recent 50 free-text comments. Each row carries a "Mark fixed"
 * pill that stamps `learn_guide_meta.last_fixed_at`; subsequent
 * feedback dated after that stamp is highlighted POST-FIX in the
 * recent-comments list.
 *
 * Backend: GET /api/learn/feedback/stats (admin-only).
 * Renders nothing for non-admin users.
 */
export default function AdminLearnGuideFeedbackButton({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busyGuide, setBusyGuide] = useState(null);

  if (user?.role !== 'admin') return null;

  const fetchStats = async (preserveOpen = false) => {
    setLoading(true);
    if (!preserveOpen) {
      setData(null);
      setOpen(true);
    }
    try {
      const { data: resp } = await axios.get(`${API}/learn/feedback/stats`, { withCredentials: true });
      setData(resp);
    } catch (err) {
      toast.error('Could not load Learn-Guide feedback', {
        description: err?.response?.data?.detail || err?.message,
      });
      if (!preserveOpen) setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const markFixed = async (guidePath, clear = false) => {
    setBusyGuide(guidePath);
    try {
      await axios.post(
        `${API}/learn/guide/mark-fixed`,
        { guide_path: guidePath, clear },
        { withCredentials: true },
      );
      const label = labelFromPath(guidePath);
      toast.success(clear ? `Reset "fixed" stamp for ${label}` : `Marked "${label}" as fixed`);
      await fetchStats(true);
    } catch (err) {
      toast.error('Could not update fixed stamp', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyGuide(null);
    }
  };

  // Merge every guide that either has feedback OR has a fixed stamp,
  // then sort with "needs attention" first (highest downs, then lowest
  // % helpful), so the admin sees the pain first.
  const rows = (() => {
    const guides = new Set([
      ...Object.keys(data?.by_guide || {}),
      ...Object.keys(data?.last_fixed_map || {}),
    ]);
    const arr = Array.from(guides).map((gp) => {
      const s = data?.by_guide?.[gp] || { up: 0, down: 0, latest: null, last_fixed_at: null };
      const total = (s.up || 0) + (s.down || 0);
      const pct = total > 0 ? Math.round((s.up / total) * 100) : null;
      return {
        gp,
        label: labelFromPath(gp),
        up: s.up || 0,
        down: s.down || 0,
        total,
        pct,
        latest: s.latest,
        lastFixedAt: s.last_fixed_at || data?.last_fixed_map?.[gp] || null,
      };
    });
    arr.sort((a, b) => {
      // Downs desc first, then %-helpful asc, then label asc.
      if (b.down !== a.down) return b.down - a.down;
      const aPct = a.pct == null ? 999 : a.pct;
      const bPct = b.pct == null ? 999 : b.pct;
      if (aPct !== bPct) return aPct - bPct;
      return a.label.localeCompare(b.label);
    });
    return arr;
  })();

  const grandTotal = rows.reduce((acc, r) => acc + r.total, 0);
  const grandUp = rows.reduce((acc, r) => acc + r.up, 0);
  const grandDown = rows.reduce((acc, r) => acc + r.down, 0);

  return (
    <>
      <button
        type="button"
        onClick={() => fetchStats(false)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white transition-colors"
        data-testid="admin-learn-guide-feedback-btn"
        title="View Learn-Guide page feedback (admin)"
      >
        <GraduationCap className="w-3.5 h-3.5" />
        Learn-Guide Feedback
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
          onClick={() => setOpen(false)}
          data-testid="admin-learn-guide-feedback-modal"
        >
          <div
            className="bg-card border rounded-xl shadow-2xl w-full max-w-3xl relative max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
              <h3 className="text-base font-bold flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-violet-500" />
                Learn-Guide Feedback
              </h3>
              <ModalCloseButton onClick={() => setOpen(false)} testId="admin-learn-guide-feedback-close" />
            </div>

            <div className="p-4 overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading stats…
                </div>
              )}

              {!loading && data && (
                <>
                  <div className="mb-4 flex flex-wrap items-center gap-3 text-xs">
                    <span className="text-muted-foreground">Total responses:</span>
                    <span className="font-mono font-bold">{grandTotal}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                      <ThumbsUp className="w-3 h-3" /> {grandUp}
                    </span>
                    <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-400">
                      <ThumbsDown className="w-3 h-3" /> {grandDown}
                    </span>
                    <span className="text-muted-foreground ml-auto">
                      {rows.length} guide{rows.length === 1 ? '' : 's'} with activity
                    </span>
                  </div>

                  {rows.length > 0 ? (
                    <table className="w-full text-sm" data-testid="admin-learn-guide-feedback-table">
                      <thead className="sticky top-0 bg-card z-10">
                        <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider border-b">
                          <th className="py-2 font-semibold">Guide</th>
                          <th className="py-2 font-semibold text-right">Up</th>
                          <th className="py-2 font-semibold text-right">Down</th>
                          <th className="py-2 font-semibold text-right">Total</th>
                          <th className="py-2 font-semibold text-right">% helpful</th>
                          <th className="py-2 font-semibold text-right">Fixed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr
                            key={r.gp}
                            className="border-b last:border-b-0"
                            data-testid={`admin-learn-guide-feedback-row-${r.gp}`}
                          >
                            <td className="py-2 font-medium">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <a
                                  href={r.gp}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="hover:underline text-foreground"
                                  title={`Open ${r.gp}`}
                                >
                                  {r.label}
                                </a>
                                <a
                                  href={r.gp}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-muted-foreground hover:text-foreground shrink-0"
                                  title="Open guide in new tab"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                              <div className="text-[10px] text-muted-foreground/70 font-mono truncate" title={r.gp}>{r.gp}</div>
                            </td>
                            <td className="py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">{r.up}</td>
                            <td className="py-2 text-right font-mono text-rose-700 dark:text-rose-400">{r.down}</td>
                            <td className="py-2 text-right font-mono">{r.total}</td>
                            <td className="py-2 text-right font-mono">
                              {r.pct == null
                                ? <span className="text-muted-foreground">—</span>
                                : <span className={r.pct >= 75 ? 'text-emerald-700 dark:text-emerald-400'
                                    : r.pct >= 50 ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-rose-700 dark:text-rose-400'}>{r.pct}%</span>}
                            </td>
                            <td className="py-2 text-right">
                              {r.lastFixedAt ? (
                                <div className="inline-flex items-center gap-1.5">
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
                                    title={`Guide marked fixed on ${r.lastFixedAt}`}
                                  >
                                    <CheckCircle2 className="w-2.5 h-2.5" />
                                    {r.lastFixedAt.slice(0, 10)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => markFixed(r.gp, true)}
                                    disabled={busyGuide === r.gp}
                                    className="inline-flex items-center justify-center w-5 h-5 rounded text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 hover:bg-muted disabled:opacity-50"
                                    title="Reset fixed stamp"
                                    data-testid={`admin-learn-guide-feedback-reset-${r.gp}`}
                                  >
                                    {busyGuide === r.gp
                                      ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                      : <RotateCcw className="w-2.5 h-2.5" />}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => markFixed(r.gp, false)}
                                  disabled={busyGuide === r.gp}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 hover:bg-amber-500/30 text-amber-700 dark:text-amber-300 border border-amber-500/30 disabled:opacity-50 transition-colors"
                                  title="Mark this guide as fixed — future feedback will be tagged POST-FIX"
                                  data-testid={`admin-learn-guide-feedback-mark-${r.gp}`}
                                >
                                  {busyGuide === r.gp
                                    ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                    : <Wrench className="w-2.5 h-2.5" />}
                                  Mark fixed
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center text-sm text-muted-foreground py-6">
                      No feedback yet — the Learn guides haven&apos;t collected any thumbs.
                    </p>
                  )}

                  {(data?.recent_comments || []).length > 0 && (
                    <div className="mt-5">
                      <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recent comments</h4>
                      <ul className="space-y-2">
                        {data.recent_comments.map((c, i) => {
                          const segments = parseCommentSegments(c.comment || '');
                          const fixedAt = data?.last_fixed_map?.[c.guide_path];
                          const isPostFix = !!(fixedAt && c.updated_at && c.updated_at > fixedAt);
                          return (
                            <li
                              key={i}
                              className={`text-xs border rounded p-2 bg-background ${isPostFix ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : ''}`}
                              data-testid={`admin-learn-guide-feedback-comment-${i}`}
                            >
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <a
                                  href={c.guide_path}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-mono font-bold text-violet-700 dark:text-violet-300 hover:underline truncate max-w-[260px]"
                                  title={c.guide_path}
                                >
                                  {labelFromPath(c.guide_path)}
                                </a>
                                {c.rating === 'up'
                                  ? <ThumbsUp className="w-3 h-3 text-emerald-600" />
                                  : <ThumbsDown className="w-3 h-3 text-rose-600" />}
                                {isPostFix && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40"
                                    title={`Submitted after this guide was marked fixed (${fixedAt?.slice(0, 10)})`}
                                    data-testid={`admin-learn-guide-feedback-comment-${i}-post-fix-badge`}
                                  >
                                    <CheckCircle2 className="w-2.5 h-2.5" /> Post-fix
                                  </span>
                                )}
                                <span className="text-muted-foreground ml-auto">{c.updated_at?.slice(0, 10)}</span>
                              </div>
                              <p className="text-foreground/80 leading-relaxed break-words">
                                {segments.map((seg, idx) => seg.type === 'ticker' ? (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                      if (typeof onStockClick === 'function') {
                                        setOpen(false);
                                        setTimeout(() => onStockClick(seg.value), 60);
                                      }
                                    }}
                                    className="inline-flex items-center font-mono font-bold text-[11px] px-1.5 py-0.5 mx-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 hover:bg-violet-200 dark:hover:bg-violet-900/60 transition-colors align-baseline"
                                    title={`Open ${seg.value} stock details`}
                                    data-testid={`admin-learn-guide-feedback-ticker-chip-${seg.value}-${i}`}
                                  >
                                    {seg.value}
                                  </button>
                                ) : (
                                  <span key={idx}>{seg.value}</span>
                                ))}
                              </p>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
